<template>
  <div class="fractional-cd-slider-root">
    <pvd-field-group>
      <pvd-label :id="sliderLabel" pvd-class="valueSelectedLabel" :for="id">
        <p class="valueSelected" :class="labelSize">
          <span :id="leftSliderValue" class="slider-value valueSelectedLeft">{{ selectedValue[0] }}</span>
          <span :id="rightSliderValue" class="slider-value valueSelectedRight"
            >{{ selectedValue[1] }}
            <span class="inline-block">
              <span v-if="selectedValue[1] == '--'" class="screen-reader-only">No rate available</span>
              <pvd-popover
                pvd-disable-trigger-slot-button
                pvd-aria-describedby="apy-tooltip-text"
                pvd-aria-labelledby="apy-tooltip-heading"
                v-show="selectedValue[1] && selectedValue[1] != '--'">
                <div class="pvd-popover__popover-content-reset">
                  <h3 id="apy-tooltip-heading">{{ clientConstants.APY_TOOLTIP_HEADING }}</h3>
                  <p id="apy-tooltip-text">{{ clientConstants.APY_TOOLTIP_TEXT }}</p>
                </div>
                <pvd-link
                  slot="trigger"
                  pvd-aria-label="Fractional CD APY annotation"
                  pvd-icon-right="action__help"
                  pvd-trigger></pvd-link>
              </pvd-popover>
            </span>
          </span>
        </p>
      </pvd-label>
      <slider
        :id="id"
        :min="0"
        :max="5"
        :step="1"
        :value="currentValueKey"
        :values="values"
        @sliderValueChanged="handleSliderValueChanged($event)" />
    </pvd-field-group>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Slider, { Options, Values } from './Slider.vue';
import { ClientConstants, Duration } from './../client.constants';

export default defineComponent({
  name: 'FractionalCdSliderComponent',
  components: { Slider },
  props: {
    id: { type: String, required: true },
    values: {
      type: Array as PropType<Options>,
      required: true,
      validator(obj: { key: number; leftValue: string | Duration; rightValue: string | Duration }) {
        return obj.key !== null && obj.leftValue !== null && obj.rightValue !== null;
      }
    },
    currentValue: { type: Number, default: 0, required: false },
    labelSize: { type: String, default: 'normal', required: false }
  },
  data: () => {
    return {
      clientConstants: ClientConstants,
      currentValueKey: 0
    };
  },
  computed: {
    /*
     * checks for the value in the values array passed in from the parent
     * returns the value if found, empty string if not
     */
    selectedValue(): string[] {
      const pair = this.values.find((obj: Values) => obj.key === this.currentValueKey);
      if (pair) {
        return pair.rightValue ? [pair.leftValue, pair.rightValue] : [pair.leftValue, ''];
      }
      return ['', ''];
    },
    sliderLabel(): string {
      return `${this.id}-label`;
    },
    leftSliderValue(): string {
      return `${this.id}-left-value`;
    },
    rightSliderValue(): string {
      return `${this.id}-right-value`;
    }
  },
  methods: {
    handleSliderValueChanged(value: string) {
      this.currentValueKey = Number(value);
    }
  },
  mounted() {
    this.currentValueKey = this.currentValue;
  }
});
</script>

<style scoped lang="scss">
@import '@fmr-pr101742/providence-visual-style/styles/visual-style.scss';

.fractional-cd-slider-root {
  min-width: 100%;

  @media only screen and (min-width: $mobile-extra-small) and (max-width: $mobile-small) {
    min-width: 18rem;
  }

  @media only screen and (max-width: $mobile-extra-small) {
    min-width: 15rem;
  }
}

.valueSelected {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  margin-bottom: 0;
}

.large {
  .slider-value {
    @include pvd-heading-level-4;

    @include pvd-breakpoint('at-768') {
      @include pvd-heading-level-3;
    }
  }
}

.normal {
  .slider-value {
    @include pvd-text-large();
  }
}

.valueSelectedLeft {
  font-weight: $pvd-font-weight-regular !important;
}

.valueSelectedRight {
  font-weight: $pvd-font-weight-bold;
}

.inline-block {
  display: inline-block;
}
</style>
